import {
  Box,
  Button,
  IconButton,
  Modal,
  Typography,
  useMediaQuery,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { DateTime } from "luxon";
import ScheduleRONComponent from "../permitComponents/contractor/ScheduleRONComponent";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import { updatePermitPackage } from "../../store/actions/dataActions";
import { useNavigate } from "react-router-dom";
import { convertTimezoneFromObj } from "../../functions/helperFunctions";

const ScheduleContractorRonModal = ({
  open,
  setOpen,
  selectedRow,
  setRonStarted,
  setSnackOpen,
  ownerData,
  ownerZone,
  fiveMinBeforeSession = null,
}) => {
  const navigate = useNavigate();
  const currentSession = useSelector((state) => state.sessions.currentSession);
  const currentUser = useSelector((state) => state[currentSession]);
  const guestUser = useSelector((state) => state.guestSession);

  const dispatch = useDispatch();
  const [scheduledDate, setScheduledDate] = useState(null);
  const [isScheduled, setIsScheduled] = useState(false);
  const [ronDate, setRonDate] = useState("");
  const [sessionUrl, setSessionUrl] = useState(false);
  const [onlySign, setOnlySign] = useState(false);

  const base = useMemo(
    () => ({
      instantError: "",
      isInstantAvailable: false,
      checkInstantLoading: false,
      expeditedSelectedBtn: false,
      furtherSelectedBtn: false,
      yesLoading: false,
      instantData: {},
      furtherData: {},
      instantTimeLeft: 60,
      startInstantTimer: false,
      immediateRequested: false,
      instantAccepted: false,
      canceledInstant: false,
      immediateLock: false,
      submitting: false,
      slots: [],
      checkDateLoading: false,
      startTimer: false,
      furtherRequested: false,
      scheduleTimeLeft: 60,
      isAvailable: false,
      furtherError: "",
      tempDate: null,
      tempTime: null,
      unavailableText: "",
      furtherDateObj: null,
      isFurtherAvailable: false,
      slotIndex: -1,
      firstTimer: true,
      submitError: "",
      timeUpTriggered: false,
      furtherTimeUpTriggered: false,
    }),
    []
  );

  const [schedule, setSchedule] = useState(base);

  const mobileSize = useMediaQuery((theme) => theme.breakpoints.down("sm"));
  const tabletSize = useMediaQuery((theme) => theme.breakpoints.down("md"));

  const contractorNotarization =
    selectedRow?.processing_permit?.notarization?.contractor?.notarization;

  const cancelRef = useRef(false);
  const handleTimeUpCalledRef = useRef(false); // 🚫 Prevent reentry

  useEffect(() => {
    if (contractorNotarization?.only_sign && onlySign === false) {
      setOnlySign(true);
    } else if (!contractorNotarization?.only_sign && onlySign === true) {
      setOnlySign(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contractorNotarization?.only_sign]);

  const dispatchInitialRon = useCallback(async () => {
    let actionModule;
    if (currentSession === "subUserSession") {
      actionModule = await import("../../store/actions/subUserActions");
    } else {
      actionModule = await import("../../store/actions/userActions");
    }
    dispatch(actionModule.setInstantRonInitialState());
  }, [currentSession, dispatch]);

  useEffect(() => {
    if (schedule?.instantError) {
      const timer = setTimeout(() => {
        setSchedule((prev) => ({
          ...prev,
          instantError: "",
        }));
      }, 10000);
      return () => clearTimeout(timer);
    }
  }, [schedule?.instantError]);

  //! FINAL //////////////////////////////////////////////////////
  const startInstantRon = useCallback(async () => {
    setSchedule((prev) => ({
      ...prev,
      submitError:
        "** Warning ** You should not leave the page or refresh the page while we are assigning you a notary. The process will take less than a minute.",
    }));

    let payload = null;
    if (ownerData) {
      payload = {
        task_id: selectedRow?.task_id,
        cst_packageID: selectedRow?.cst_packageID,
        signeeEmail: ownerData["email"],
        currentSession: currentSession,
        name: ownerData["name"],
        signatoryTitle: ownerData["signatoryTitle"],
        authority: ownerData["authority"],
        phone: ownerData["phone"],
        ownerAddress: ownerData["ownerAddress"],
        address: ownerData["address"],
        city: ownerData["city"],
        state: ownerData["state"],
        zipCode: ownerData["zipCode"],
        owners: ownerData["owners"],
        dob: "",
        idType: ownerData["idType"],
        timezone: ownerZone,
      };
    } else {
      payload = {
        task_id: selectedRow?.task_id,
        cst_packageID: selectedRow?.cst_packageID,
        currentSession: currentSession,
        timezone: "America/New_York",
      };
    }

    try {
      const resp = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/nocApi/notary/start-instant-contractor-ron`,
        { payload },
        { withCredentials: true }
      );

      if (resp.data.success === true) {
        if (["guestSession"].includes(currentSession)) {
          navigate("/signee/manage");
          return;
        }
        setSchedule((prev) => ({
          ...prev,
          instantError: "",
          startInstantTimer: false,
          immediateRequested: false,
        }));

        dispatchInitialRon();
        const permit = resp?.data?.data;
        await dispatch(updatePermitPackage(permit));

        const userZone = currentUser?.subuser
          ? currentUser?.subuser?.timezone
          : currentUser?.user?.personal?.[0]?.timezone;

        const dateString = convertTimezoneFromObj(
          permit?.processing_permit?.notarization?.contractor?.notarization
            ?.appointments?.booked[0],
          userZone || "America/New_York",
          "cccc, LLLL dd, yyyy hh:mm a"
        );
        setRonDate(dateString);
        setSessionUrl(
          permit?.processing_permit?.notarization?.contractor?.notarization
            ?.bn_session?.signer_url
        );
        setIsScheduled(true);
        setRonStarted(true);
        setSnackOpen(true);
        setSchedule(base);
      } else {
        setSchedule((prev) => ({
          ...prev,
          instantAccepted: false,
          immediateLock: false,
          submitError: "",
          immediateRequested: false, // Reset submitting state on error
        }));
        dispatchInitialRon();
      }
    } catch (e) {
      setSchedule((prev) => ({
        ...prev,
        submitError: "",
        immediateRequested: false, // Reset submitting state on exception
      }));
      if (e?.response?.data?.error === "stop") {
        setSchedule((prev) => ({
          ...prev,
          slots: [],
          slotIndex: -1,
          isInstantAvailable: false,
          instantAccepted: false,
          checkInstantLoading: false,
          isAvailable: false,
          instantData: {},
          firstTimer: true,
          instantError: e?.response?.data?.message,
          immediateRequested: false,
          canceledInstant: false,
          immediateLock: false,
          startInstantTimer: false,
          instantTimeLeft: 0,
        }));
        dispatchInitialRon();
      } else {
        dispatchInitialRon();
        setSchedule((prev) => ({
          ...prev,
          immediateLock: false,
        }));
      }
    }
  }, [
    ownerData,
    ownerZone,
    selectedRow?.task_id,
    selectedRow?.cst_packageID,
    currentSession,
    currentUser,
    dispatch,
    navigate,
    dispatchInitialRon,
    setSnackOpen,
    setRonStarted,
    base,
  ]);
  //! //////////////////////////////////////////////////////////////

  //! EXPEDITED RON FUNC
  const handleImmediateRON = async () => {
    if (schedule?.isInstantAvailable) {
      setSchedule((prev) => ({
        ...prev,
        checkInstantLoading: true,
      }));
      try {
        const payload = {
          task_id: selectedRow?.task_id,
          currentSession: currentSession,
          instantData: schedule?.instantData,
          instant: true,
          timezone: "America/New_York",
          cst_packageID: selectedRow?.cst_packageID,
        };

        const resp = await axios.post(
          `${process.env.REACT_APP_BASE_URL}/nocApi/notary/request-immediate-ron`,
          {
            payload,
          },
          {
            withCredentials: true,
          }
        );
        if (resp.data.success) {
          // Reset timer ref when starting new instant timer
          handleTimeUpCalledRef.current = false;

          setSchedule((prev) => ({
            ...prev,
            instantTimeLeft: 300,
            startInstantTimer: true,
            immediateRequested: true,
          }));
        }
      } catch (e) {}
      setSchedule((prev) => ({
        ...prev,
        checkInstantLoading: false,
      }));
    } else {
      setSchedule((prev) => ({
        ...prev,
        checkInstantLoading: true,
        yesLoading: true,
      }));
      setScheduledDate(null);

      const dateTime = DateTime.now().setZone("America/New_York");

      const formattedObject = {
        day_of_month: dateTime.day,
        month: dateTime.month,
        year: dateTime.year,
        time: dateTime.toFormat("HH:mm"),
      };

      const payload = {
        task_id: selectedRow?.task_id,
        cst_packageID: selectedRow?.cst_packageID,
        ask_date: formattedObject,
        instant: true,
        timezone: "America/New_York",
        currentSession: currentSession,
      };

      try {
        const resp = await axios.post(
          `${process.env.REACT_APP_BASE_URL}/nocApi/notary/available-ron-schedule`,
          {
            payload,
          },
          {
            withCredentials: true,
          }
        );
        if (resp.data.success) {
          // Reset timer ref when starting new instant timer
          handleTimeUpCalledRef.current = false;

          setSchedule((prev) => ({
            ...prev,
            isInstantAvailable: true,
            instantData: resp.data,
            // immediateRequested: true,
            instantTimeLeft: 60,
            startInstantTimer: true,
          }));
        } else {
          setSchedule((prev) => ({
            ...prev,
            instantError: resp.data.message,
          }));
        }
      } catch (e) {
        if (e?.response?.data?.action === "redirect_owner") {
          navigate("/signee/manage");
        }
      } finally {
        setSchedule((prev) => ({
          ...prev,
          checkInstantLoading: false,
          yesLoading: false,
        }));
      }
    }
  };
  //! /////////////////////

  //! FURTHER DATE ////////////////////////////////////
  const handleStartClick = async () => {
    if (schedule?.isFurtherAvailable) {
      setSchedule((prev) => ({
        ...prev,
        checkDateLoading: true,
      }));

      try {
        const payload = {
          task_id: selectedRow?.task_id,
          currentSession: currentSession,
          instantData: schedule?.furtherData,
          instant: true,
          timezone: "America/New_York",
          further: schedule?.furtherSelectedBtn,
          cst_packageID: selectedRow?.cst_packageID,
        };
        const resp = await axios.post(
          `${process.env.REACT_APP_BASE_URL}/nocApi/notary/request-immediate-ron`,
          {
            payload,
          },
          {
            withCredentials: true,
          }
        );

        if (resp.data.success) {
          setSchedule((prev) => ({
            ...prev,
            scheduleTimeLeft: 300,
            startTimer: true,
            furtherRequested: true,
          }));
        }
      } catch (e) {}
      setSchedule((prev) => ({
        ...prev,
        checkDateLoading: false,
      }));
    } else {
      setSchedule((prev) => ({
        ...prev,
        checkDateLoading: true,
      }));
      setScheduledDate(null);
      const payload = {
        task_id: selectedRow?.task_id,
        cst_packageID: selectedRow?.cst_packageID,
        ask_date: schedule?.furtherDateObj,
        instant: true,
        timezone: "America/New_York",
        currentSession: currentSession,
        is_further: schedule?.furtherSelectedBtn,
      };
      try {
        const resp = await axios.post(
          `${process.env.REACT_APP_BASE_URL}/nocApi/notary/available-ron-schedule`,
          {
            payload,
          },
          {
            withCredentials: true,
          }
        );
        if (
          resp.data.isAvailable?.length === 0 &&
          resp.data?.slots?.length > 0
        ) {
          setSchedule((prev) => ({
            ...prev,
            scheduleTimeLeft: 60,
            startTimer: true,
            instantAccepted: false,
            isFurtherAvailable: false,
            unavailableText:
              "The selected date is unavailable. Please choose a different date or select one from the list below.",
            slots: resp.data.slots,
          }));
        } else if (resp.data.success) {
          setSchedule((prev) => ({
            ...prev,
            isFurtherAvailable: true,
            furtherData: resp.data,
            scheduleTimeLeft: 60,
            startTimer: true,
            slots: [],
          }));
        } else {
          setSchedule((prev) => ({
            ...prev,
            furtherError: resp.data.message,
            scheduleTimeLeft: 60,
            firstTimer: false,
            isAvailable: false,
            instantAccepted: false,
            unavailableText:
              "The selected date is unavailable. Please choose a different date.",
            slots: [],
          }));
        }
      } catch (e) {
        if (e?.response?.data?.action === "redirect_owner") {
          navigate("/signee/manage");
        }
      } finally {
        setSchedule((prev) => ({
          ...prev,
          checkDateLoading: false,
        }));
      }
    }
  };

  const { immediateRequested, canceledInstant } = schedule || {};

  // Check both current user (contractors) and guest user (owners) for instant RON acceptance
  const contractorInstantRonAccepted = currentUser?.instantRonAccepted || false;
  const ownerInstantRonAccepted = guestUser?.instantRonAccepted || false;
  const instantRonAccepted =
    contractorInstantRonAccepted || ownerInstantRonAccepted;

  const handleTimeUp = useCallback(
    async (type = "further") => {
      if (
        (type === "further" && cancelRef.current) ||
        (type === "instant" && handleTimeUpCalledRef.current)
      ) {
        return;
      }

      if (type === "further") {
        cancelRef.current = true;
      } else if (type === "instant") {
        handleTimeUpCalledRef.current = true;
      }

      const payload = {
        task_id: selectedRow?.task_id,
        cst_packageID: selectedRow?.cst_packageID,
        currentSession,
      };

      try {
        setSchedule((prev) => ({
          ...prev,
          slots: [],
          slotIndex: -1,
          isInstantAvailable: false,
          isAvailable: false,
          instantData: {},
          unavailableText: "",
          firstTimer: true,
        }));
        setScheduledDate(null);

        if (immediateRequested === true) {
          if (canceledInstant === false && instantRonAccepted === false) {
            setSchedule((prev) => ({
              ...prev,
              instantError:
                "Unfortunately, we have not received any response from the notaries at this moment. We apologize for the inconvenience as they are currently very busy. Please consider scheduling an appointment for a later time. Thank you for your understanding.",
            }));
          }

          setSchedule((prev) => ({
            ...prev,
            immediateRequested: false,
            canceledInstant: false,
          }));
        }

        await axios.post(
          `${process.env.REACT_APP_BASE_URL}/nocApi/notary/clear-ron-schedules`,
          { payload },
          { withCredentials: true }
        );
      } catch (e) {}

      setSchedule((prev) => ({
        ...prev,
        startTimer: false,
        startInstantTimer: false,
        timeUpTriggered: false,
        furtherTimeUpTriggered: false,
        immediateRequested: false,
      }));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      selectedRow?.task_id,
      selectedRow?.cst_packageID,
      setSchedule,
      setScheduledDate,
    ]
  );

  useEffect(() => {
    return () => {
      cancelRef.current = false;
      handleTimeUpCalledRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (schedule?.tempDate && schedule?.tempTime) {
      // Extract values from tempDate and tempTime
      const year = schedule?.tempDate.year();
      const month = schedule?.tempDate.month() + 1; // Adjust for zero-indexed months
      const day = schedule?.tempDate.date();
      const hour = schedule?.tempTime.hour();
      const minute = schedule?.tempTime.minute();

      // Ensure all values are numbers and are valid
      if (
        !isNaN(year) &&
        !isNaN(month) &&
        !isNaN(day) &&
        !isNaN(hour) &&
        !isNaN(minute)
      ) {
        const dateObj = {
          day_of_month: day,
          month: month,
          year: year,
          time: schedule?.tempTime.format("HH:mm"), // Formats the time in 'HH:mm' format
        };

        // Convert tempDate and tempTime to the user's timezone
        const userDateTime = DateTime.fromObject(
          {
            year,
            month,
            day,
            hour,
            minute,
            second: 0, // Set seconds to 0 by default
          },
          { zone: "America/New_York" }
        );

        // Check if the date and time is in the past
        if (userDateTime < DateTime.now().setZone("America/New_York")) {
          setSchedule((prev) => ({
            ...prev,
            unavailableText: "Selected datetime is in the past.",
            furtherDateObj: null,
          }));
        } else {
          setSchedule((prev) => ({
            ...prev,
            furtherDateObj: dateObj,
            unavailableText: "",
          }));
          setScheduledDate(dateObj);
        }
      } else {
        // If any part of the date or time is invalid, set an appropriate message
        setSchedule((prev) => ({
          ...prev,
          unavailableText: "Invalid date or time selected.",
          furtherDateObj: null,
        }));
      }
    }
  }, [schedule?.tempDate, schedule?.tempTime]);

  useEffect(() => {
    let intervalId;

    if (schedule?.startTimer && schedule?.scheduleTimeLeft > 0) {
      intervalId = setInterval(() => {
        setSchedule((prev) => {
          const newTimeLeft = prev.scheduleTimeLeft - 1;
          const updates = { ...prev, scheduleTimeLeft: newTimeLeft };

          if (newTimeLeft <= 0 && cancelRef.current) {
            cancelRef.current = false;
            updates.furtherTimeUpTriggered = true;
            updates.furtherData = {};
            updates.furtherDateObj = {};
            updates.furtherSelectedBtn = false;
            updates.isFurtherAvailable = false;
            handleTimeUp("further"); // safe async
          }

          return updates;
        });
      }, 1000);
    }

    return () => clearInterval(intervalId);
  }, [schedule?.startTimer, handleTimeUp, schedule?.scheduleTimeLeft]);

  useEffect(() => {
    let intervalIdInstant;

    if (schedule?.startInstantTimer && schedule?.instantTimeLeft > 0) {
      intervalIdInstant = setInterval(() => {
        setSchedule((prev) => {
          const newTimeLeft = prev.instantTimeLeft - 1;
          const updates = { ...prev, instantTimeLeft: newTimeLeft };

          if (newTimeLeft <= 0) {
            // Always reset UI state immediately when timer expires
            updates.instantData = {};
            updates.expeditedSelectedBtn = false;
            updates.isInstantAvailable = false;
            updates.startInstantTimer = false;

            // Call cleanup function if not already called
            if (!handleTimeUpCalledRef.current) {
              // Use setTimeout to call handleTimeUp outside the setState callback
              setTimeout(() => {
                handleTimeUp("instant");
              }, 0);
            }
          }

          return updates;
        });
      }, 1000);
    }

    return () => clearInterval(intervalIdInstant);
  }, [schedule?.startInstantTimer, handleTimeUp, schedule?.instantTimeLeft]);

  useEffect(() => {
    if (
      (schedule?.scheduleTimeLeft === 0 || schedule?.scheduleTimeLeft < 0) &&
      !schedule?.furtherTimeUpTriggered &&
      !cancelRef.current
    ) {
      // LOCK before running
      cancelRef.current = true;

      // Still set the flag (optional if you’re using the ref instead)
      setSchedule((prev) => ({
        ...prev,
        furtherTimeUpTriggered: true,
      }));
      cancelRef.current = false;
      handleTimeUp("further");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    schedule?.scheduleTimeLeft,
    schedule?.furtherTimeUpTriggered,
    handleTimeUp,
  ]);

  useEffect(() => {
    if (
      (schedule?.instantTimeLeft === 0 || schedule?.instantTimeLeft < 0) &&
      !handleTimeUpCalledRef.current
    ) {
      handleTimeUp("instant");
    }
  }, [schedule?.instantTimeLeft, handleTimeUp]);

  useEffect(() => {
    if (
      schedule?.submitError !== "" &&
      schedule?.submitError !==
        "** Warning ** You should not leave the page or refresh the page while we are assigning you a notary. The process will take less than a minute."
    ) {
      setSchedule((prev) => ({
        ...prev,
        submitting: false,
      }));
      const interv = setInterval(() => {
        setSchedule((prev) => ({
          ...prev,
          submitError: "",
        }));
      }, 5000);
      return () => clearInterval(interv);
    }
  }, [schedule?.submitError]);

  useEffect(() => {
    if (instantRonAccepted === true && schedule?.immediateLock === false) {
      setSchedule((prev) => ({
        ...prev,
        immediateLock: true,
        instantAccepted: true,
      }));
      startInstantRon();
    } else if (
      schedule?.instantAccepted === true &&
      instantRonAccepted === false
    ) {
      setSchedule((prev) => ({
        ...prev,
        instantAccepted: false,
      }));
    }
  }, [
    instantRonAccepted,
    schedule?.immediateLock,
    schedule?.instantAccepted,
    startInstantRon,
  ]);

  return (
    <Modal
      open={open}
      onClose={() => {
        handleTimeUpCalledRef.current = false;
        cancelRef.current = false;
      }}
      sx={{ overflow: "auto" }}
    >
      <Box
        sx={{
          overflowY: "none",
          position: "relative",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          maxHeight: "90%",
          height: mobileSize ? "80%" : "auto",
          maxWidth: "70rem",
          bgcolor: "background.paper",
          borderRadius: "18px",
          boxShadow: 24,
          px: mobileSize ? 1 : 4,
          py: 2,
          animation: "fadeIn 0.5s ease-in-out",
        }}
      >
        <Typography
          id="modal-modal-title"
          variant={mobileSize ? "h5" : "h4"}
          align="center"
          component="h2"
          color="primary"
        >
          {onlySign
            ? "Your Documents are Now Prepared and Ready for Signing"
            : isScheduled
              ? "Your Remote Online Notarization (RON) Session is Scheduled"
              : "Schedule your Remote Online Notarization (RON) Session"}
        </Typography>
        <Typography
          align="center"
          variant="body2"
          color="primary"
          fontWeight="bold"
          mb={2}
        >
          {onlySign
            ? "Please proceed to sign the documents at your convenience. Once you have signed, the owner will be able to initiate the notarization process."
            : !isScheduled &&
              "Choose a date that is convenient for you. Once the date and time have been selected, we will contact the notaries to see if they are available."}
        </Typography>
        <Box sx={{ position: "absolute", right: 10, top: 10 }}>
          <IconButton
            disabled={currentUser?.instantRonAccepted}
            onClick={() => {
              setOpen(false);
              if (schedule?.startInstantTimer || schedule?.startTimer) {
                handleTimeUp();
              }
              setSchedule(base);
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
        <Box
          sx={{
            maxHeight: "35.3rem", // Adjust the max height as needed
            overflowY: "auto",
            height: mobileSize ? "calc(90vh - 280px)" : "calc(90vh - 410px)",
            border: "2px solid #d3d3d3",
            mb: 5,
            p: 2,
            borderRadius: "18px",
            boxShadow: "3px 4px 12px rgba(0, 0, 0, 0.3)", // Added box shadow
          }}
        >
          {onlySign ? (
            <Box>
              <Box sx={{ display: "flex", justifyContent: "center" }}>
                <img
                  alt="avatar_circle"
                  src="/img/website_img/pp_avatar_circle.png"
                  style={{ width: "18rem", height: "auto", zIndex: 2 }}
                />
              </Box>
              <Box sx={{ display: "flex", justifyContent: "center" }}>
                <Button
                  onClick={() => {
                    window.open(
                      selectedRow?.processing_permit?.notarization?.contractor
                        ?.notarization?.bn_session?.signer_url,
                      "_blank"
                    );
                    setOpen(false);
                  }}
                  sx={{ ml: 2.5, zIndex: 1 }}
                  size="large"
                  variant="contained"
                >
                  SIGN YOUR DOCUMENTS
                </Button>
              </Box>
            </Box>
          ) : (
            <ScheduleRONComponent
              handleImmediateRON={handleImmediateRON}
              schedule={schedule}
              scheduledDate={scheduledDate}
              setScheduledDate={setScheduledDate}
              setSchedule={setSchedule}
              isScheduled={isScheduled}
              ronDate={ronDate}
              fiveMinBeforeSession={fiveMinBeforeSession}
              setOpen={setOpen}
              sessionUrl={sessionUrl}
              handleStartClick={handleStartClick}
              cancelRef={cancelRef}
              handleTimeUp={handleTimeUp}
              handleTimeUpCalledRef={handleTimeUpCalledRef}
            />
          )}
        </Box>
        <Box
          sx={{
            borderBottomLeftRadius: "18px",
            borderBottomRightRadius: "18px",
            backgroundColor: "#fff",
            position: "fixed",
            bottom: -20,
            left: 0,
            width: "100%",
            // borderTop: "3px solid #d3d3d3",
            py: 1,
            animation: "slideUp 0.5s ease-in-out",
          }}
        >
          <Box sx={{ display: "flex", justifyContent: "center", mb: 1 }}>
            <Typography
              sx={{ mt: 1, mb: 1, px: 2 }}
              fontWeight="bold"
              align="center"
              variant={tabletSize ? "body2" : "h6"}
              color="error"
            >
              {schedule?.submitError || schedule?.instantError}
            </Typography>
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};

export default React.memo(ScheduleContractorRonModal);
